import { widgetFetch, uploadWithProgress, configureWidgetClient, setOidcTokenProvider } from './ofetch-client'
import type { SubmitTicketData, TagInfo, TicketResult, TrackedTicketResult } from './types'
import type {
  CategoryItem as SdkCategoryItem,
  WidgetConfig as SdkWidgetConfig,
  ProjectListItem as SdkProjectListItem,
  AttachmentMeta as SdkAttachmentMeta,
  TicketDetail as SdkTicketDetail,
  TicketListItem as SdkTicketListItem,
  WidgetUser as SdkWidgetUser,
} from '@reqdesk/sdk-core'

// Re-export client configuration
export { configureWidgetClient as configureClient, setOidcTokenProvider }

// Legacy compat — setBearerToken is no longer needed (ofetch interceptor handles it)
export function setBearerToken(_token: string | null) {
  // No-op: token is now fetched per-request via oidcTokenProvider
}

function generateIdempotencyKey(): string {
  return crypto.randomUUID()
}

// ── JSON:API response shape ──────────────────────────────────────────────────

interface ApiResponse<T> {
  responseCode: string
  responseMessage: string
  httpCode: number
  data: T
  meta?: Record<string, unknown>
  included?: unknown[]
}

// ── Ticket Submission ────────────────────────────────────────────────────────

export async function submitTicket(projectId: string, data: SubmitTicketData): Promise<TicketResult> {
  const res = await widgetFetch<ApiResponse<{ id: string; attributes: { ticketNumber: string; status: string } }> & { meta?: { trackingToken?: string } }>(
    `/projects/${projectId}/tickets`,
    {
      method: 'POST',
      body: {
        title: data.title,
        description: data.description,
        priority: data.priority,
        categoryId: data.categoryId,
        email: data.email,
        clientMetadata: data.clientMetadata,
        tagIds: data.tagIds && data.tagIds.length > 0 ? data.tagIds : undefined,
      },
      headers: { 'Idempotency-Key': generateIdempotencyKey() },
    },
  )

  return {
    id: res.data.id,
    ticketNumber: res.data.attributes.ticketNumber,
    trackingToken: res.meta?.trackingToken as string | undefined,
    status: res.data.attributes.status,
  }
}

// ── Tracking ─────────────────────────────────────────────────────────────────

export async function trackTicket(token: string): Promise<TrackedTicketResult> {
  const res = await widgetFetch<ApiResponse<{ id: string; attributes: { ticketNumber: string; title: string; status: string; priority: string; createdAt: string } }>>(`/tickets/track?token=${encodeURIComponent(token)}`)

  const included = (res.included ?? []) as Array<{ id: string; type: string; attributes: Record<string, unknown> }>

  return {
    id: res.data.id,
    ticketNumber: res.data.attributes.ticketNumber,
    title: res.data.attributes.title,
    status: res.data.attributes.status,
    priority: res.data.attributes.priority,
    createdAt: res.data.attributes.createdAt,
    replies: included
      .filter(i => i.type === 'public-reply')
      .map(i => ({
        id: i.id,
        body: i.attributes.body as string,
        authorName: i.attributes.authorName as string,
        isStaff: i.attributes.isStaff as boolean,
        createdAt: i.attributes.createdAt as string,
      })),
  }
}

export async function submitTrackingReply(token: string, body: string): Promise<void> {
  await widgetFetch('/tickets/track/reply', {
    method: 'POST',
    body: { trackingToken: token, body },
  })
}

// ── Categories ───────────────────────────────────────────────────────────────

/** @deprecated since 1.4 — re-exported from `@reqdesk/sdk-core`. Kept here so existing
 *  imports `import { CategoryItem } from '@reqdesk/sdk-widget'` keep working. */
export type CategoryItem = SdkCategoryItem

export async function getCategories(projectId: string, parentId?: string | null): Promise<CategoryItem[]> {
  const params = new URLSearchParams({ 'filter[active]': 'true' })
  if (parentId === null || parentId === undefined) params.set('filter[parentId]', 'root')
  else params.set('filter[parentId]', parentId)

  const res = await widgetFetch<ApiResponse<Array<{ id: string; attributes: { name: string; parentId?: string; hasChildren: boolean } }>>>(
    `/projects/${projectId}/categories?${params}`,
  )
  const items = Array.isArray(res.data) ? res.data : []
  return items.map(c => ({
    id: c.id,
    name: c.attributes.name,
    parentId: c.attributes.parentId,
    hasChildren: c.attributes.hasChildren,
  }))
}

export async function createCategory(projectId: string, name: string, parentId?: string | null): Promise<CategoryItem> {
  const res = await widgetFetch<ApiResponse<{ id: string; attributes: { name: string; parentId?: string; hasChildren: boolean } }>>(
    `/projects/${projectId}/categories/widget`,
    {
      method: 'POST',
      body: { name, parentId: parentId ?? undefined },
    },
  )
  return {
    id: res.data.id,
    name: res.data.attributes.name,
    parentId: res.data.attributes.parentId,
    hasChildren: res.data.attributes.hasChildren,
  }
}

// ── Tags ─────────────────────────────────────────────────────────────────────

export async function listProjectTags(projectId: string): Promise<TagInfo[]> {
  const res = await widgetFetch<ApiResponse<Array<{ id: string; attributes: { name: string; color: string } }>>>(
    `/projects/${projectId}/tags?sort=sort_order`,
  )
  const items = Array.isArray(res.data) ? res.data : []
  return items.map(t => ({
    id: t.id,
    name: t.attributes.name,
    color: t.attributes.color || '#6b7280',
  }))
}

export async function createTag(projectId: string, name: string, color?: string): Promise<TagInfo> {
  const res = await widgetFetch<ApiResponse<{ id: string; attributes: { name: string; color: string } }>>(
    `/projects/${projectId}/tags/widget`,
    {
      method: 'POST',
      body: { name, color },
    },
  )
  return {
    id: res.data.id,
    name: res.data.attributes.name,
    color: res.data.attributes.color || '#6b7280',
  }
}

// ── Widget Config ───────────────────────────────────────────────────────────

/** @deprecated since 1.4 — re-exported from `@reqdesk/sdk-core`. Kept here so existing
 *  imports `import { WidgetConfig } from '@reqdesk/sdk-widget'` keep working. */
export type WidgetConfig = SdkWidgetConfig

export async function getWidgetConfig(projectId: string): Promise<WidgetConfig> {
  const res = await widgetFetch<ApiResponse<{ id: string; type: string; attributes: Omit<WidgetConfig, 'id'> }>>(
    `/projects/${projectId}/widget-config`,
  )
  const attrs = res.data.attributes
  return {
    id: res.data.id,
    ...attrs,
    allowedAuthModes: Array.isArray(attrs.allowedAuthModes) && attrs.allowedAuthModes.length > 0
      ? attrs.allowedAuthModes
      : ['sso', 'email'],
    signedIdentityTtlMinutes: typeof attrs.signedIdentityTtlMinutes === 'number' && attrs.signedIdentityTtlMinutes > 0
      ? attrs.signedIdentityTtlMinutes
      : 10,
  }
}

// ── Projects (OIDC multi-project) ───────────────────────────────────────────

/** @deprecated since 1.4 — re-exported from `@reqdesk/sdk-core`. */
export type ProjectListItem = SdkProjectListItem

export async function listProjects(workspaceId?: string): Promise<ProjectListItem[]> {
  const params = new URLSearchParams({ 'page[size]': '50' })
  if (workspaceId) params.set('filter[workspaceId]', workspaceId)

  const res = await widgetFetch<ApiResponse<Array<{ id: string; attributes: { name: string; slug: string } }>>>(
    `/projects?${params}`,
  )
  const items = Array.isArray(res.data) ? res.data : []
  return items.map(p => ({
    id: p.id,
    name: p.attributes.name,
    slug: p.attributes.slug,
  }))
}

export async function closeTicket(ticketId: string): Promise<void> {
  await widgetFetch(`/tickets/${ticketId}/status`, {
    method: 'PUT',
    body: { status: 'resolved' },
  })
}

// ── Attachments ──────────────────────────────────────────────────────────────

/** @deprecated since 1.4 — re-exported from `@reqdesk/sdk-core`. */
export type AttachmentMeta = SdkAttachmentMeta

export function uploadAttachment(
  ticketId: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<AttachmentMeta> {
  return uploadWithProgress(`/api/v1/tickets/${ticketId}/attachments`, file, onProgress) as Promise<AttachmentMeta>
}

export function uploadReplyAttachment(
  ticketId: string,
  replyId: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<AttachmentMeta> {
  return uploadWithProgress(`/api/v1/tickets/${ticketId}/replies/${replyId}/attachments`, file, onProgress) as Promise<AttachmentMeta>
}

export async function getAttachmentDownloadUrl(attachmentId: string): Promise<{ downloadUrl: string; expiresAt: string }> {
  return widgetFetch(`/attachments/${attachmentId}/download`)
}

// ── Ticket Detail ────────────────────────────────────────────────────────────

/** @deprecated since 1.4 — re-exported from `@reqdesk/sdk-core`. */
export type TicketDetail = SdkTicketDetail

export async function getTicketDetail(ticketId: string, includeMedia = true): Promise<TicketDetail> {
  const include = includeMedia ? '?include=media' : ''
  const res = await widgetFetch<ApiResponse<{
    id: string
    attributes: {
      ticketNumber: string
      title: string
      description?: string
      status: string
      priority: string
      timestamps: { created: string; modified: string }
    }
  }>>(`/tickets/${ticketId}${include}`)

  const included = (res.included ?? []) as Array<{
    id: string
    type: string
    attributes: Record<string, unknown>
    relationships?: Record<string, { data?: { type?: string; id?: string } | null }>
  }>

  // Parse all attachments first so we can both (a) attach them to the ticket aggregate and
  // (b) route reply-scoped attachments onto their parent reply.
  const allAttachments = included
    .filter(i => i.type === 'attachment')
    .map(i => ({
      raw: i,
      meta: {
        id: i.id,
        fileName: i.attributes.fileName as string,
        contentType: i.attributes.contentType as string,
        fileSize: i.attributes.fileSize as number,
        downloadUrl: i.attributes.downloadUrl as string | undefined,
        downloadUrlExpiresAt: i.attributes.downloadUrlExpiresAt as string | undefined,
      },
    }))

  // Group attachments by their parent reply id. The backend marks reply-scoped attachments by
  // setting `attributes.replyId` (a widget-specific field added in backend commit that pairs
  // with widget 1.2.14). Falls back to the JSON:API relationship form when present, then to
  // ticket-level otherwise — matches the pre-v1.2.14 behaviour where all attachments were
  // assumed to be the ticket's.
  const attachmentsByReplyId = new Map<string, AttachmentMeta[]>()
  const ticketAttachments: AttachmentMeta[] = []
  for (const entry of allAttachments) {
    const attrReplyId = entry.raw.attributes?.replyId as string | undefined
    const replyRel = entry.raw.relationships?.reply?.data
    const replyId = attrReplyId
      ?? (replyRel && replyRel.id && (replyRel.type === 'reply' || replyRel.type === 'ticket-reply')
        ? replyRel.id
        : undefined)
    if (replyId) {
      const list = attachmentsByReplyId.get(replyId) ?? []
      list.push(entry.meta)
      attachmentsByReplyId.set(replyId, list)
    } else {
      ticketAttachments.push(entry.meta)
    }
  }

  const replies = included
    .filter(i => i.type === 'reply' || i.type === 'ticket-reply')
    .map(i => {
      const attachments = attachmentsByReplyId.get(i.id)
      return {
        id: i.id,
        body: i.attributes.body as string,
        authorName: i.attributes.authorName as string ?? 'Anonymous',
        isStaff: i.attributes.isStaff as boolean ?? false,
        createdAt: (i.attributes.timestamps as Record<string, string>)?.created ?? '',
        attachments: attachments && attachments.length > 0 ? attachments : undefined,
      }
    })

  const tags = included
    .filter(i => i.type === 'tag')
    .map(i => ({
      id: i.id,
      name: i.attributes.name as string,
      color: (i.attributes.color as string | undefined) || '#6b7280',
    }))

  return {
    id: res.data.id,
    ticketNumber: res.data.attributes.ticketNumber,
    title: res.data.attributes.title,
    description: res.data.attributes.description,
    status: res.data.attributes.status,
    priority: res.data.attributes.priority,
    createdAt: res.data.attributes.timestamps?.created ?? '',
    replies,
    attachments: ticketAttachments,
    tags,
  }
}

export async function submitReply(ticketId: string, body: string): Promise<{ id: string }> {
  const res = await widgetFetch<ApiResponse<{ id: string }>>(`/tickets/${ticketId}/replies`, {
    method: 'POST',
    body: { body, isInternal: false },
  })
  return { id: res.data.id }
}

// ── My Tickets ───────────────────────────────────────────────────────────────

/** @deprecated since 1.4 — re-exported from `@reqdesk/sdk-core`. */
export type WidgetUser = SdkWidgetUser

export async function resolveWidgetUser(projectId: string, email: string): Promise<WidgetUser | null> {
  try {
    return await widgetFetch<WidgetUser>(`/projects/${projectId}/tickets/widget-users?email=${encodeURIComponent(email)}`)
  } catch {
    return null
  }
}

/** @deprecated since 1.4 — re-exported from `@reqdesk/sdk-core`. */
export type TicketListItem = SdkTicketListItem

export async function listMyTickets(
  projectId: string,
  userId: string,
  page = 1,
  pageSize = 20,
  showAll = false,
): Promise<TicketListItem[]> {
  const params = new URLSearchParams({
    'sort': '-created_at',
    'page[number]': String(page),
    'page[size]': String(pageSize),
  })
  // When showAll is false, filter by the user's own tickets
  if (!showAll && userId) {
    params.set('filter[createdBy]', userId)
  }

  const res = await widgetFetch<ApiResponse<Array<{
    id: string
    attributes: {
      ticketNumber: string
      title: string
      status: string
      priority: string
      timestamps: { created: string; modified: string }
    }
  }>>>(`/projects/${projectId}/tickets?${params}`)

  const items = Array.isArray(res.data) ? res.data : []
  return items.map(t => ({
    id: t.id,
    ticketNumber: t.attributes.ticketNumber,
    title: t.attributes.title,
    status: t.attributes.status,
    priority: t.attributes.priority,
    createdAt: t.attributes.timestamps?.created ?? '',
  }))
}
